import { useRef, useState } from 'react'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded'
import LayersRoundedIcon from '@mui/icons-material/LayersRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import { Checkbox, CountBadge, RailChipTabs, RailPageHeader, type RailChipTabItem } from '../Common'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { FloatingMenu } from '../Common/FloatingMenu'
import type { CalendarRangeMode, CalendarStyleId, TimelineBoardKey } from '../../types/schema'
import { TIMELINE_BOARD_KEYS } from '../../types/schema'
import { CALENDAR_STYLES } from './calendarThemes'

interface CalendarHeaderProps {
  styleId: CalendarStyleId
  onStyleChange: (next: CalendarStyleId) => void
  range: CalendarRangeMode
  onRangeChange: (next: CalendarRangeMode) => void
  focusedDate: Date
  onFocusedDateChange: (next: Date) => void
  activeBoard: TimelineBoardKey
  boardScope: { mode: 'current' | 'custom'; boardKeys?: TimelineBoardKey[] }
  onBoardScopeChange: (next: { mode: 'current' | 'custom'; boardKeys?: TimelineBoardKey[] }) => void
  isFilterOpen: boolean
  onToggleFilter: () => void
  isMobile?: boolean
  /** Mobile-only: opens the unified Options bottom sheet (Boards / Style / Filter). */
  onOpenOptions?: () => void
  /** CSS custom-property map applied on each FloatingMenu so the portal-rendered
   *  dropdown inherits the calendar's kind colors. */
  menuVars?: Record<string, string>
}

const RANGE_LABEL: Record<CalendarRangeMode, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
}

const RANGE_ITEMS: RailChipTabItem<CalendarRangeMode>[] = (['day', 'week', 'month'] as CalendarRangeMode[]).map((mode) => ({
  key: mode,
  label: RANGE_LABEL[mode],
}))

const BOARD_LABEL: Record<TimelineBoardKey, string> = {
  DASHBOARD: 'Dashboard',
  VISIONBOARD: 'Visionboard',
  ARCHIVE: 'Archive',
  BIN: 'Bin',
}

const stepFocusedDate = (range: CalendarRangeMode, focused: Date, delta: number): Date => {
  const next = new Date(focused.getFullYear(), focused.getMonth(), focused.getDate())
  if (range === 'day') next.setDate(next.getDate() + delta)
  else if (range === 'week') next.setDate(next.getDate() + delta * 7)
  else next.setMonth(next.getMonth() + delta)
  return next
}

const fmtAnchor = (date: Date, range: CalendarRangeMode): string => {
  if (range === 'day') {
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  }
  if (range === 'week') {
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  }
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

export const CalendarHeader = ({
  styleId,
  onStyleChange,
  range,
  onRangeChange,
  focusedDate,
  onFocusedDateChange,
  activeBoard,
  boardScope,
  onBoardScopeChange,
  isFilterOpen,
  onToggleFilter,
  isMobile = false,
  onOpenOptions,
  menuVars,
}: CalendarHeaderProps) => {
  const styleButtonRef = useRef<HTMLButtonElement | null>(null)
  const scopeButtonRef = useRef<HTMLButtonElement | null>(null)
  const [isStyleOpen, setIsStyleOpen] = useState(false)
  const [isScopeOpen, setIsScopeOpen] = useState(false)

  const handleToday = () => onFocusedDateChange(new Date())

  const activeStyle = CALENDAR_STYLES.find((style) => style.id === styleId) ?? CALENDAR_STYLES[0]
  const visibleStyles = CALENDAR_STYLES
  const additionalBoards = (boardScope.boardKeys ?? []).filter((key) => key !== activeBoard)

  const setBoardChecked = (board: TimelineBoardKey, checked: boolean) => {
    if (board === activeBoard) return
    const baseSet = new Set([activeBoard, ...additionalBoards])
    if (checked) baseSet.add(board)
    else baseSet.delete(board)
    const nextKeys = Array.from(baseSet)
    onBoardScopeChange({
      mode: nextKeys.length > 1 ? 'custom' : 'current',
      boardKeys: nextKeys,
    })
  }

  if (isMobile) {
    const extraBoardsCount = additionalBoards.length
    return (
      <header className="calHeader calHeader--mobile">
        <span className="sr-only">Calendar</span>
        <div className="calHeaderMobileRow calHeaderMobileRow--anchor">
          <IconButton
            variant="tertiary"
            size="md"
            label="Previous"
            icon={<ChevronLeftRoundedIcon fontSize="small" />}
            onClick={() => onFocusedDateChange(stepFocusedDate(range, focusedDate, -1))}
          />
          <Pressable
            className="calHeaderMobileAnchor"
            onClick={handleToday}
            title="Jump to today"
            aria-label={`${fmtAnchor(focusedDate, range)}. Tap to jump to today.`}
          >
            <span className="calHeaderMobileAnchorLabel">{fmtAnchor(focusedDate, range)}</span>
            <span className="calHeaderMobileAnchorToday">Today</span>
          </Pressable>
          <IconButton
            variant="tertiary"
            size="md"
            label="Next"
            icon={<ChevronRightRoundedIcon fontSize="small" />}
            onClick={() => onFocusedDateChange(stepFocusedDate(range, focusedDate, 1))}
          />
          <IconButton
            variant="tertiary"
            size="md"
            pressed={isFilterOpen}
            onClick={onOpenOptions}
            label="Calendar options"
            icon={(
              <>
                <TuneRoundedIcon fontSize="small" />
                {extraBoardsCount > 0 && (
                  <CountBadge value={extraBoardsCount + 1} size="sm" />
                )}
              </>
            )}
          />
        </div>
        <div className="calHeaderMobileRow calHeaderMobileRow--range">
          <RailChipTabs<CalendarRangeMode>
            className="calHeaderRangeTabs--wide"
            ariaLabel="Calendar range"
            orientation="horizontal"
            value={range}
            onChange={(key) => onRangeChange(key as CalendarRangeMode)}
            items={RANGE_ITEMS}
          />
        </div>
      </header>
    )
  }

  return (
    <RailPageHeader
      className="calHeader"
      badge="C"
      subtitle="Schedule & events"
      title="Calendar"
      titleClassName="calHeaderTitle"
      center={(
        <div className="railPageHeaderControlGroup">
          <Button variant="tertiary" size="sm" onClick={handleToday}>Today</Button>
          <div className="calHeaderNav">
            <IconButton
              variant="tertiary"
              size="sm"
              aria-label="Previous"
              label="Previous"
              icon={<ChevronLeftRoundedIcon fontSize="small" />}
              onClick={() => onFocusedDateChange(stepFocusedDate(range, focusedDate, -1))}
            />
            <IconButton
              variant="tertiary"
              size="sm"
              aria-label="Next"
              label="Next"
              icon={<ChevronRightRoundedIcon fontSize="small" />}
              onClick={() => onFocusedDateChange(stepFocusedDate(range, focusedDate, 1))}
            />
          </div>
          <RailChipTabs<CalendarRangeMode>
            ariaLabel="Calendar range"
            orientation="horizontal"
            value={range}
            onChange={(key) => onRangeChange(key as CalendarRangeMode)}
            items={RANGE_ITEMS}
          />
        </div>
      )}
      actions={(
        <>
          <Button
            ref={scopeButtonRef}
            variant="tertiary"
            size="sm"
            onClick={() => setIsScopeOpen((current) => !current)}
            aria-expanded={isScopeOpen}
            title="Boards in view"
            icon={<LayersRoundedIcon fontSize="small" />}
          >
            {additionalBoards.length > 0 ? `Boards (${additionalBoards.length + 1})` : 'This board'}
          </Button>
          <FloatingMenu
            anchorRef={scopeButtonRef}
            isOpen={isScopeOpen}
            onClose={() => setIsScopeOpen(false)}
            placement="bottom-end"
            className="calScopeMenu"
            role="menu"
            ariaLabel="Boards in view"
            style={menuVars as React.CSSProperties}
          >
            <p className="calMenuLabel">Boards in view</p>
            {TIMELINE_BOARD_KEYS.map((board) => {
              const isCurrent = board === activeBoard
              const isChecked = isCurrent || additionalBoards.includes(board)
              return (
                <Checkbox
                  key={board}
                  wrapperClassName="calMenuRow"
                  checked={isChecked}
                  disabled={isCurrent}
                  onChange={(next) => setBoardChecked(board, next)}
                  label={(
                    <>
                      <span>{BOARD_LABEL[board]}</span>
                      {isCurrent && <small>current</small>}
                    </>
                  )}
                />
              )
            })}
          </FloatingMenu>

          <Button
            ref={styleButtonRef}
            variant="tertiary"
            size="sm"
            onClick={() => setIsStyleOpen((current) => !current)}
            aria-expanded={isStyleOpen}
            title="Calendar style"
            icon={<PaletteRoundedIcon fontSize="small" />}
          >
            {activeStyle.label}
          </Button>
          <FloatingMenu
            anchorRef={styleButtonRef}
            isOpen={isStyleOpen}
            onClose={() => setIsStyleOpen(false)}
            placement="bottom-end"
            className="calStyleMenu"
            role="menu"
            ariaLabel="Calendar style"
            style={menuVars as React.CSSProperties}
          >
            <p className="calMenuLabel">Style</p>
            <div className="calStyleSwatchGrid">
              {visibleStyles.map((style) => (
                <Pressable
                  key={style.id}
                  className={`calStyleSwatch${style.id === styleId ? ' isActive' : ''}`}
                  aria-pressed={style.id === styleId}
                  onClick={() => {
                    onStyleChange(style.id)
                    setIsStyleOpen(false)
                  }}
                >
                  <span className="calStyleSwatchChip" style={{ background: style.swatch }} aria-hidden="true" />
                  <span>{style.label}</span>
                </Pressable>
              ))}
            </div>
          </FloatingMenu>

          <IconButton
            variant="tertiary"
            size="sm"
            pressed={isFilterOpen}
            onClick={onToggleFilter}
            label="Filter items"
            icon={<TuneRoundedIcon fontSize="small" />}
          />
        </>
      )}
    />
  )
}
